<template>
  <app-loading-section
    :count="loadedData.length"
    :loading="loadingData"
    :heading="emptyHeading"
    :is-empty-boxed="isEmptyBoxed"
  >
    <template #message>
      <slot name="emptyMessage"></slot>
    </template>

    <b-table
      hoverable

      :data="loadedData"
      :detailed="detailed"

      :paginated="true"
      :per-page="paginationInfo.per_page"
      :current-page="paginationInfo.current_page"
      :total="paginationInfo.total"
      :page-input="pageInput"
      page-input-position="is-input-right"
      :debounce-page-input="1000"

      :checkable="isCheckable"
      :sticky-checkbox="stickyCheckbox"
      :checked-rows="checkedRowsData"

      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"

      @sort="onSort"
      @page-change="pageChanged"
      @check="rowsChecked"
      @click="clickedRow"

      :backend-sorting="serverPaginated"
      :backend-pagination="serverPaginated"

      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortInfo.field, sortInfo.order]"

      :narrowed="isNarrow"
      :scrollable="isScrollable"
      :mobile-cards="hasMobileCards"
      :class="{'has-small-text': isSmallText}"
    >
      <template v-for="column in columns">
        <b-table-column
          v-slot="props"
          :field="column.field"
          :label="column.label"
          :width="column.width || 120"
          :numeric="column.isNumeric"
          :sortable="column.sortable || false"
          :sticky="column.sticky"
          :visible="column.visible"
        >
          <sensitive-data-wrapper v-if="column.isSensitiveDate">
            <span
              v-html="display(props, column)"
              @click="onClick(props, column)"
            ></span>
          </sensitive-data-wrapper>
          <span
            v-else
            v-html="display(props, column)"
            @click="onClick(props, column)"
          ></span>
        </b-table-column>
      </template>


      <template v-if="!withoutActions">
        <b-table-column cell-class="action_cell" :width="actionsWidth || 120" v-slot="props">
          <slot name="actions" v-bind:row="props.row"></slot>
        </b-table-column>
      </template>

      <slot></slot>

      <template v-slot:bottom-left>
        <p class="has-text-light">Showing {{ paginationInfo.from }} to {{ paginationInfo.to }} of {{ paginationInfo.total }} {{ entriesLabel ? entriesLabel : 'entries' }}</p>

        <div v-if="showPerPageOption">
          <b-select
            size="is-small ml-3"
            v-model="paginationInfo.per_page"
            @input="perPageChanged"
          >
            <option value="10">10 per page</option>
            <option value="25">25 per page</option>
            <option value="50">50 per page</option>
            <option value="100">100 per page</option>
            <option value="500">500 per page</option>
          </b-select>
        </div>

      </template>
    </b-table>

  </app-loading-section>
</template>

<script>
import utils from "../../services/utils";
import SensitiveDataWrapper from "../SensitiveDataWrapper.vue";

export default {
  name: "TablePaginated",
  components: {SensitiveDataWrapper},
  props: {
    loadData: {
      type: Function,
      require: true
    },
    columnScope: {
      type: Object,
    },
    checkedRowsData: {
      type: Array,
      default: () => []
    },
    /**
     * Column Example
     *
     *
     {
          // Required can be an empty string
          label: 'Full Name',

          // Required if you are not using display
          field: 'full_name',

          // Defaults to 120
          width: '300',

          // Used to overwrite the default empty value of '--'
          default: 'Unknown',

          // If is a number then set this to true
          isNumeric: true,

          // display is not necessary if you have the field, its just a way to customize if necessary
          display(row) {
            return row.full_name;
          }
        },
     *
     *
     *
     */
    columns: {
      type: Array,
      default: [],
    },
    emptyHeading: {
      type: String,
      default: "Empty",
    },
    isEmptyBoxed: {
      type: Boolean,
      default: false,
    },
    withoutActions: {
      type: Boolean,
      default: false,
    },
    actionsWidth: {
      type: Number,
      default: 120,
    },
    defaultSort: {
      type: [
        String,
        Array
      ],
    },
    detailed: {
      type: Boolean,
      default: false,
    },
    showPerPageOption: {
      type: Boolean,
      default: false,
    },
    perPage: {
      type: Number,
      default: 15,
    },
    entriesLabel: String,
    isNarrow: {
      type: Boolean,
      default: false,
    },
    isScrollable: {
      type: Boolean,
      default: false,
    },
    isCheckable: {
      type: Boolean,
      default: false,
    },
    stickyCheckbox: {
      type: Boolean,
      default: false,
    },
    isSmallText: {
      type: Boolean,
      default: false,
    },
    pageInput: {
      type: Boolean,
      default: false,
    },
    hasMobileCards: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      utilsDebounceTimout: null,
      loadedData: [],
      loadingData: true,
      serverPaginated: true,
      defaultSortOrder: "asc",
      sortInfo: {
        field: "",
        order: "asc",
      },
      paginationInfo: {
        current_page: 1,
        from: 1,
        last_page: 2,
        path: "http://flippilot.local/v2/records/archived/3",
        per_page: this.perPage,
        to: 1,
        total: 0,
      },
    };
  },
  mounted() {
    this._handleDefaultSort();
    this._loadData();
  },
  computed: {
    defaultParent() {
      return  this.$parent.$parent.$parent.$parent;
    },
    vm() {
      return this.columnScope || this.defaultParent;
    },
    searchableColumns() {
      return this.columns.filter(c => c.searchable);
    }
  },
  methods: {
    display(props, column) {
      let value = "";
      if (column.display) {
        value = column.display(props.row, this.vm);
      } else if (column.field) {
        value = props.row[column.field];
      }

      let defaultString = "--";
      if (column.default) {
        defaultString = column.default;
      }

      if (column.isNumeric) {
        return value;
      }

      if (typeof value !== "string") {
        return defaultString;
      }

      return utils.stringIsEmpty(value) ? defaultString : value;
    },
    clickedRow(event) {
      this.$emit('clickedRow', event);
    },
    onClick(props, column) {
      if (column.onClick) {
        return column.onClick(props.row, this.vm);
      }
    },
    pageChanged(page) {
      this.paginationInfo.current_page = page;
      this._loadData();
    },
    perPageChanged() {
      this.loadingData = true;
      this.queryData();
    },
    onSort(field, order) {
      this.sortInfo.field = field;
      this.sortInfo.order = order;
      this._loadData();
    },
    rowsChecked(rows) {
      this.$emit('rowsChecked', rows)
    },
    _handleDefaultSort() {
      if (Array.isArray(this.defaultSort)) {
        this.sortInfo.field = this.defaultSort[0];
        this.sortInfo.order = this.defaultSort[1];
        return;
      }

      this.sortInfo.field = this.defaultSort;
    },
    reloadData() {
      return this._loadData();
    },
    _loadData() {
      this.loadingData = true;

      let sortField = "";
      let sortOrder = "";
      if (this.sortInfo.field && this.sortInfo.order) {
        sortField = `sort_field=${this.sortInfo.field}`;
        sortOrder = `sort_order=${this.sortInfo.order}`;
      }

      const query = [
        sortField,
        sortOrder,
        `page=${this.paginationInfo.current_page}`,
        `per_page=${this.paginationInfo.per_page}`
      ].filter(item => item.length).join("&");

      return this.loadData(query).then((response) => {
        // Checking to see if its server paginated data
        if (response.data.data && response.data.links && response.data.meta) {
          this.serverPaginated = true;
          this.loadedData = response.data.data;
          this.paginationInfo.to = response.data.meta.to;
          this.paginationInfo.from = response.data.meta.from;
          this.paginationInfo.total = response.data.meta.total;
          this.paginationInfo.path = response.data.meta.path;
        } else {
          this.serverPaginated = false;
          this.loadedData = response.data;
          this.paginationInfo.to = response.data.meta.to;
          this.paginationInfo.from = response.data.meta.from;
          this.paginationInfo.total = response.data.length;
          this.paginationInfo.path = response.data.meta.path;
        }

        this.$emit("loaded-data", this.loadedData);
        this.loadingData = false;
      }).catch(error => {
        this.loadingData = false;
        this.$emit("failed-to-load-data", error);
      });
    },
    queryData() {
      // always reset the page to 1
      // this is mostly called for filtering or resetting the table
      this.paginationInfo.current_page = 1;
      this.utilsDebounceTimout = utils.debounce(this._loadData, this.utilsDebounceTimout, 500);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../styles/variables/variables";

::v-deep(.has-small-text) {
  font-size: 0.825rem;
}

::v-deep(.b-table .table th.is-sticky) {
  background: $white;
}
</style>
